import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  Input,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Spin,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useDebounceValue } from '@/hooks/useDebounceValue';
import {
  previewEmployeeNotification,
  searchNoticePeople,
  sendEmployeeNotification,
} from '@/services/emailTemplates.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import EmailPreviewFrame from './EmailPreviewFrame';
import {
  EMAIL_TEMPLATES_QUERY_KEY,
  EMPLOYEE_NOTICE_QUERY_KEY,
} from './constants';
import useEmployeeNoticesT, { errorText } from './useEmployeeNoticesT';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Server default; the preview says the real cap.
const DEFAULT_MAX_EXTRA = 20;
// "Add address" asks the server for people of the company from this length on.
const PEOPLE_SEARCH_MIN_LENGTH = 2;

/**
 * One group of recipients (CC or BCC): the resolved ones with a checkbox each
 * (unchecked = left out of this send) and the addresses added by hand.
 */
function RecipientGroup({
  title,
  hint,
  people,
  added,
  excluded,
  onToggle,
  onToggleAll,
  onRemove,
  action = null,
}) {
  const { tn } = useEmployeeNoticesT();
  const included = people.filter((p) => !excluded.has(p.email)).length;
  return (
    <div className="rounded-xl border border-zinc-200 p-3">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Checkbox
          checked={people.length > 0 && included === people.length}
          indeterminate={included > 0 && included < people.length}
          disabled={!people.length}
          onChange={(e) => onToggleAll(people, e.target.checked)}
        />
        <span className="text-[13px] font-bold">
          {title} ({included + added.length})
        </span>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className="mb-2 text-[12px] text-zinc-500">{hint}</div>
      <div className="max-h-48 overflow-auto">
        {added.map((a) => (
          <div
            key={a.email}
            className="flex items-center gap-2 py-0.5 text-[12.5px]"
          >
            <Tag color="blue" className="!m-0">
              {tn('addedTag')}
            </Tag>
            <span className="min-w-0 flex-1 truncate">
              {a.name ? `${a.name} — ` : ''}
              {a.email}
            </span>
            <Button
              size="small"
              type="link"
              danger
              onClick={() => onRemove(a.email)}
            >
              {tn('remove')}
            </Button>
          </div>
        ))}
        {people.map((p) => (
          <label
            key={p.email}
            className="flex cursor-pointer items-center gap-2 py-0.5 text-[12.5px]"
          >
            <Checkbox
              checked={!excluded.has(p.email)}
              onChange={() => onToggle(p.email)}
            />
            <span
              className={`min-w-0 flex-1 truncate ${excluded.has(p.email) ? 'text-zinc-400 line-through' : ''}`}
            >
              {p.name ? `${p.name} — ` : ''}
              {p.email}
            </span>
          </label>
        ))}
        {!people.length && !added.length && (
          <span className="text-[12.5px] text-zinc-500">
            {tn('noneInGroup')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * "Send notice" on the employee profile: the company's template filled for
 * this employee, shown as the complete email; who gets it (CC / BCC), with
 * the option to leave people out or add addresses, and a one-off note above
 * the body. One email goes out (see the server send).
 */
export default function EmployeeNotificationModal({
  open,
  onClose,
  employeeId,
  state,
  initialTemplateId = null,
}) {
  const { t, tn } = useEmployeeNoticesT();
  const queryClient = useQueryClient();
  const templates = useMemo(() => state?.templates || [], [state?.templates]);
  const sends = state?.sends || [];
  const [templateId, setTemplateId] = useState(null);
  const [extraMessage, setExtraMessage] = useState('');
  const [excluded, setExcluded] = useState(() => new Set());
  const [added, setAdded] = useState([]);
  const [search, setSearch] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newKind, setNewKind] = useState('bcc');
  const [addError, setAddError] = useState('');
  // "Include all employees": every active employee of the company, this send only.
  const [includeAll, setIncludeAll] = useState(false);
  // "Include all HR and sector heads": every client HR and the heads of all
  // sectors in CC, this send only.
  const [includeAllVisible, setIncludeAllVisible] = useState(false);
  const debouncedMessage = useDebounceValue(extraMessage, 500);
  const debouncedAddress = useDebounceValue(newAddress, 300);

  useEffect(() => {
    if (open) {
      setTemplateId(
        (current) => initialTemplateId || current || templates[0]?._id || null
      );
    } else {
      // Kept across template switches; cleared only when the modal closes.
      setExtraMessage('');
      setExcluded(new Set());
      setAdded([]);
      setSearch('');
      setNewAddress('');
      setAddError('');
      setIncludeAll(false);
      setIncludeAllVisible(false);
    }
  }, [open, templates, initialTemplateId]);

  const preview = useQuery({
    queryKey: [
      EMAIL_TEMPLATES_QUERY_KEY,
      'preview',
      employeeId,
      templateId,
      debouncedMessage,
      includeAll,
      includeAllVisible,
    ],
    queryFn: () =>
      previewEmployeeNotification(
        employeeId,
        templateId,
        debouncedMessage,
        includeAll,
        includeAllVisible
      ),
    enabled: open && !!templateId,
    retry: false,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
  const { data } = preview;
  const recipients = useMemo(() => data?.recipients || [], [data]);
  const maxExtra = data?.maxExtraAddresses || DEFAULT_MAX_EXTRA;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? recipients.filter((r) =>
          `${r.name || ''} ${r.email}`.toLowerCase().includes(q)
        )
      : recipients;
  }, [recipients, search]);

  // A hand-added address replaces the same resolved one (the server does the same).
  const addedEmails = useMemo(
    () => new Set(added.map((a) => a.email)),
    [added]
  );
  const shown = filtered.filter((r) => !addedEmails.has(r.email));
  const totals = useMemo(() => {
    const kept = recipients.filter(
      (r) => !excluded.has(r.email) && !addedEmails.has(r.email)
    );
    const visible =
      kept.filter((r) => r.visible).length +
      added.filter((a) => a.kind === 'cc').length;
    const hidden =
      kept.length -
      kept.filter((r) => r.visible).length +
      added.filter((a) => a.kind === 'bcc').length;
    return { visible, hidden, total: visible + hidden };
  }, [recipients, excluded, added, addedEmails]);

  const toggle = (email) =>
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  const toggleAll = (people, include) =>
    setExcluded((current) => {
      const next = new Set(current);
      people.forEach((p) =>
        include ? next.delete(p.email) : next.add(p.email)
      );
      return next;
    });

  // People of the company by name, asked from the server (company and access
  // decided there, by the employee the notice is about).
  const peopleQuery = debouncedAddress.trim();
  const people = useQuery({
    queryKey: [EMAIL_TEMPLATES_QUERY_KEY, 'people', employeeId, peopleQuery],
    queryFn: ({ signal }) => searchNoticePeople(employeeId, peopleQuery, signal),
    enabled:
      open && !!employeeId && peopleQuery.length >= PEOPLE_SEARCH_MIN_LENGTH,
    retry: false,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const recipientEmails = useMemo(
    () => new Set(recipients.map((r) => r.email)),
    [recipients]
  );
  const typedEnough =
    newAddress.trim().length >= PEOPLE_SEARCH_MIN_LENGTH;
  const peopleOptions = useMemo(
    () =>
      typedEnough
        ? (people.data?.items || []).map((p) => ({
            value: p.email,
            person: p,
            disabled: addedEmails.has(p.email),
            label: (
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-semibold">{p.name || p.email}</span>
                  {p.name && (
                    <span className="text-zinc-500"> — {p.email}</span>
                  )}
                </span>
                {recipientEmails.has(p.email) && !addedEmails.has(p.email) && (
                  <span className="text-[11px] text-zinc-500">
                    {tn('alreadyRecipient')}
                  </span>
                )}
                {addedEmails.has(p.email) && (
                  <Tag color="blue" className="!m-0">
                    {tn('addedTag')}
                  </Tag>
                )}
                <Tag className="!m-0">{tn(`peopleKinds.${p.kind}`)}</Tag>
              </div>
            ),
          }))
        : [],
    [typedEnough, people.data, addedEmails, recipientEmails, tn]
  );

  // Enter on a highlighted suggestion fires the input's Enter first and then
  // the pick; the typed text is handled a tick later, and only if nothing
  // was picked in between.
  const pickedRef = useRef(false);

  const addEntry = (rawEmail, name = '') => {
    const email = String(rawEmail || '').trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) return setAddError(tn('invalidEmail'));
    if (addedEmails.has(email)) return setAddError(tn('alreadyAdded'));
    if (added.length >= maxExtra)
      return setAddError(tn('tooManyExtra', { count: maxExtra }));
    setAdded((current) =>
      current.some((a) => a.email === email)
        ? current
        : [...current, { email, name, kind: newKind }]
    );
    setNewAddress('');
    setAddError('');
    return undefined;
  };
  const addAddress = () => {
    const typed = newAddress;
    setTimeout(() => {
      if (pickedRef.current) {
        pickedRef.current = false;
        return;
      }
      addEntry(typed);
    }, 0);
  };
  const addPerson = (email, option) => {
    pickedRef.current = true;
    setTimeout(() => {
      pickedRef.current = false;
    }, 0);
    addEntry(email, option?.person?.name || '');
  };

  const send = useMutation({
    mutationFn: () =>
      sendEmployeeNotification(employeeId, templateId, {
        extraMessage,
        // Only what the server resolved may be left out.
        excluded: recipients
          .map((r) => r.email)
          .filter((email) => excluded.has(email) && !addedEmails.has(email)),
        extraCc: added.filter((a) => a.kind === 'cc').map((a) => a.email),
        extraBcc: added.filter((a) => a.kind === 'bcc').map((a) => a.email),
        includeAllEmployees: includeAll,
        includeAllVisible,
      }),
    onSuccess: (result) => {
      message.success(result?.message || tn('noticeSent'));
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_NOTICE_QUERY_KEY, employeeId],
      });
      onClose();
    },
    onError: (error) => message.error(errorText(error, tn('sendFailed'))),
  });

  const count = totals.total;
  const messageSettling =
    extraMessage !== debouncedMessage || preview.isFetching;
  // The preview on screen must match the recipients choice before sending.
  const recipientsSettling =
    !!data &&
    (Boolean(data.includeAllEmployees) !== includeAll ||
      Boolean(data.includeAllVisible) !== includeAllVisible);
  const canIncludeAll = !!data && data.recipientsMode !== 'allEmployees';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      destroyOnHidden
      title={tn('noticeTitle')}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="!rounded-[50px]" onClick={onClose}>
            {t('cancel', { ns: 'forms' })}
          </Button>
          <Popconfirm
            okButtonProps={{ className: '!rounded-[50px]' }}
            cancelButtonProps={{ className: '!rounded-[50px]' }}
            title={tn('sendConfirm', { count })}
            okText={t('yes', { ns: 'forms' })}
            cancelText={t('no', { ns: 'forms' })}
            onConfirm={() => send.mutate()}
            disabled={!count || messageSettling || recipientsSettling}
          >
            <Button
              type="primary"
              className="!rounded-[50px]"
              loading={send.isPending}
              disabled={
                !count || messageSettling || recipientsSettling || !data
              }
            >
              {sends.length ? tn('sendAgain') : tn('send')}
            </Button>
          </Popconfirm>
        </div>
      }
    >
      <div className="grid gap-3">
        {sends.length > 0 && (
          <Alert
            type="success"
            showIcon
            message={tn('alreadyNotified', {
              date: dayjs(sends[0].sentAt).format(DATE_FORMATS.DISPLAY),
              name: sends[0].sentByName || '—',
              count: sends[0].recipientCount,
            })}
          />
        )}

        {templates.length > 1 && (
          <Select
            value={templateId}
            onChange={setTemplateId}
            options={templates.map((tpl) => ({
              value: tpl._id,
              label: tpl.name,
            }))}
          />
        )}

        <div>
          <div className="mb-1 text-[13px] font-bold">{tn('extraMessage')}</div>
          <Input.TextArea
            value={extraMessage}
            onChange={(e) => setExtraMessage(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
            maxLength={5000}
            placeholder={tn('extraMessagePlaceholder')}
          />
          <div className="mt-1 text-[12px] text-zinc-500">
            {tn('extraMessageHint')}
          </div>
        </div>

        {preview.isLoading ? (
          <Skeleton active />
        ) : preview.isError && !data ? (
          <Alert
            type="error"
            showIcon
            message={errorText(preview.error, tn('previewFailed'))}
          />
        ) : data ? (
          <>
            {preview.isError && (
              <Alert
                type="error"
                showIcon
                message={errorText(preview.error, tn('previewFailed'))}
              />
            )}
            <EmailPreviewFrame
              subject={data.subject}
              html={data.html}
              loading={preview.isFetching}
            />

            <div className="text-[13px] font-bold">
              {tn('recipientsCount', {
                count: data.recipientCount,
                mode: data.includeAllEmployees
                  ? tn('recipientsModes.allEmployees')
                  : tn(`recipientsModes.${data.recipientsMode}`),
              })}
            </div>
            {recipients.length > 8 && (
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder={tn('searchRecipients')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <RecipientGroup
                title={tn('visibleRecipients')}
                hint={tn('visibleHint')}
                action={
                  <Button
                    size="small"
                    className="!rounded-[50px]"
                    type={includeAllVisible ? 'default' : 'primary'}
                    ghost={!includeAllVisible}
                    loading={recipientsSettling && preview.isFetching}
                    onClick={() => setIncludeAllVisible((current) => !current)}
                  >
                    {includeAllVisible
                      ? tn('onlyDefaultVisible')
                      : tn('includeAllVisible')}
                  </Button>
                }
                people={shown.filter((r) => r.visible)}
                added={added.filter((a) => a.kind === 'cc')}
                excluded={excluded}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onRemove={(email) =>
                  setAdded((c) => c.filter((a) => a.email !== email))
                }
              />
              <RecipientGroup
                title={tn('hiddenRecipients')}
                hint={tn('hiddenHint')}
                action={
                  canIncludeAll && (
                    <Button
                      size="small"
                      className="!rounded-[50px]"
                      type={includeAll ? 'default' : 'primary'}
                      ghost={!includeAll}
                      loading={recipientsSettling && preview.isFetching}
                      onClick={() => setIncludeAll((current) => !current)}
                    >
                      {includeAll
                        ? tn('onlyTemplateRecipients', {
                            mode: tn(`recipientsModes.${data.recipientsMode}`),
                          })
                        : tn('includeAllEmployees')}
                    </Button>
                  )
                }
                people={shown.filter((r) => !r.visible)}
                added={added.filter((a) => a.kind === 'bcc')}
                excluded={excluded}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onRemove={(email) =>
                  setAdded((c) => c.filter((a) => a.email !== email))
                }
              />
            </div>

            <div>
              <div className="mb-1 text-[13px] font-bold">
                {tn('addAddress')}
              </div>
              <div className="flex flex-wrap gap-2">
                <AutoComplete
                  className="min-w-[220px] flex-1"
                  value={newAddress}
                  options={peopleOptions}
                  defaultActiveFirstOption={false}
                  // The server already matched; show its list as it is.
                  filterOption={false}
                  onChange={(value) => {
                    setNewAddress(value || '');
                    setAddError('');
                  }}
                  onSelect={addPerson}
                  notFoundContent={
                    typedEnough ? (
                      people.isFetching ? (
                        <span className="flex items-center gap-2 text-[12.5px]">
                          <Spin size="small" /> {tn('peopleSearching')}
                        </span>
                      ) : (
                        <span className="text-[12.5px] text-zinc-500">
                          {tn('peopleNoMatch')}
                        </span>
                      )
                    ) : null
                  }
                >
                  <Input
                    status={addError ? 'error' : undefined}
                    placeholder={tn('addAddressPlaceholder')}
                    onPressEnter={addAddress}
                  />
                </AutoComplete>
                <Select
                  className="w-[170px]"
                  value={newKind}
                  onChange={setNewKind}
                  options={[
                    { value: 'cc', label: tn('asCc') },
                    { value: 'bcc', label: tn('asBcc') },
                  ]}
                />
                <Button
                  className="!rounded-[50px]"
                  onClick={addAddress}
                  disabled={!newAddress.trim()}
                >
                  {tn('add')}
                </Button>
              </div>
              {addError ? (
                <div className="mt-1 text-[12px] text-red-500">{addError}</div>
              ) : (
                <div className="mt-1 text-[12px] text-zinc-500">
                  {tn('addAddressHint')}
                </div>
              )}
            </div>

            <Alert
              type={count ? 'info' : 'warning'}
              showIcon
              message={
                count
                  ? tn('recipientsSummary', {
                      total: count,
                      visible: totals.visible,
                      hidden: totals.hidden,
                    })
                  : tn('noRecipients')
              }
              description={tn('oneEmailInfo')}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
}
